"use client";
import React, { useState, useEffect, useMemo, useRef } from "react";
import Link from "next/link";
import blackCar from "../../../assets/blackCar.webp";
import whiteCar from "../../../assets/whiteCar.avif";
import Image from "next/image";
import ButtonAnimated from "@/components/ButtonAnimated/ButtonAnimated";
import Input from "@/common/Input";
import { CarTopInfo } from "@/common/SelectionChips";
import useBackendUrl from "@/app/helpers/useBackendUrl";
import { useRouter } from "next/navigation";
import { useAuth } from "@/context/ContextProvider";
import TrustPilot from "@/common/TrustPilot";
import { showToast } from "@/app/helpers/showToast";
import { createVehicleFlow } from "@/app/helpers/vehicleStepFlow";
import { saveAnmeldenFlowPayload } from "@/app/anmelden/anmeldenFlowPayload";
import trackVehicleCreated from "@/lib/trackVehicleCreated";

export const TopSectionAutoVerkaufen = ({
  link,
  linkname,
  title,
  text,
  customNav,
  customNavLink,
  removeNav,
  buttonTitle = "Jetzt Bewertung ansehen",
  className,
  removePadding = false,
  step,
  setStep,
  selectedBrand,
  setSelectedBrand,
  selectedModel,
  setSelectedModel,
  selectedYear,
  setSelectedYear,
  selectedBauform,
  setSelectedBauform,
  selectedFuel,
  setSelectedFuel,
  selectedVariant,
  setSelectedVariant,
  selectedPs,
  setSelectedPs,
  selectedTransmission,
  setSelectedTransmission,
  selectedDoors,
  setSelectedDoors,
  selectedModelType,
  setSelectedModelType,
  selectedKmStand,
  setSelectedKmStand,
}) => {
  const { userData, isAnalyticalChecked } = useAuth();
  const api = useBackendUrl();
  const router = useRouter();

  useEffect(() => {
    if (step !== 11) return;
    router.prefetch("/anmelden");
  }, [router, step]);

  // Animation-States
  const [animateOut, setAnimateOut] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false); // verhindert Doppelklick
  // is-hidden waehrend des Step-Swaps (wie Startseite: swapInvisible) -> verhindert
  // das eine-Frame-Aufblitzen des Subtitles beim Wechsel Subtitle -> Chips.
  const [swapInvisible, setSwapInvisible] = useState(false);
  const [isChevronAnimating, setIsChevronAnimating] = useState(false);
  // NEU: State, um zu tracken, ob handleManualAdd gerade läuft
  const [isManualSubmitting, setIsManualSubmitting] = useState(false);
  const pendingRouteRef = useRef("");
  const didNavigateRef = useRef(false);
  const guestRedirectTimerRef = useRef(null);
  const guestRecoveryTimerRef = useRef(null);

  // Roh-Daten für die Dropdowns
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [years, setYears] = useState([]);
  const [bauformen, setBauformen] = useState([]);
  const [fuels, setFuels] = useState([]);
  const [variants, setVariants] = useState([]);
  const [psOptions, setPsOptions] = useState([]);
  const [gearboxOptions, setGearboxOptions] = useState([]);
  const [doorOptions, setDoorOptions] = useState([]);
  const [modelTypeOptions, setModelTypeOptions] = useState([]);

  // Aktuelle Eingabe-Strings (Autocomplete-Input)
  const [brandInput, setBrandInput] = useState("");
  const [modelInput, setModelInput] = useState("");
  const [yearInput, setYearInput] = useState("");
  const [bauformInput, setBauformInput] = useState("");
  const [fuelInput, setFuelInput] = useState("");
  const [variantInput, setVariantInput] = useState("");
  const [psInput, setPsInput] = useState("");
  const [transmissionInput, setTransmissionInput] = useState("");
  const [doorsInput, setDoorsInput] = useState("");
  const [modelTypeInput, setModelTypeInput] = useState("");
  const [kmStandInput, setKmStandInput] = useState("");
  const [inputError, setInputError] = useState("");
  // Fehlermeldung beim Step-Wechsel zuruecksetzen (nicht am falschen Feld haengen lassen)
  useEffect(() => {
    setInputError("");
  }, [step]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [filteredItemsStep, setFilteredItemsStep] = useState(0);

  const setFilteredForStep = (items, stepIndex) => {
    setFilteredItems(items);
    setFilteredItemsStep(stepIndex);
  };

  // ----------------------------------------
  // 2) Animation Helpers
  // ----------------------------------------
  // Ziel-Step + Fade-Status als Refs (wie Startseite): erlaubt "Fade zuerst"
  // beim Zurueck (getBackStep ist async) ohne Flackern.
  const nextStepRef = useRef(null);
  const fadeOutDoneRef = useRef(false);

  const handleFadeOut = () => {
    fadeOutDoneRef.current = false;
    setAnimateOut(true);
    setIsAnimating(true);
  };

  // Step-Swap + Fade-In ausloesen, sobald das Ziel feststeht.
  // WICHTIG (wie Startseite): waehrend des Swaps wird der Slot unsichtbar
  // geschaltet (swapInvisible). Sonst entfernt setAnimateOut(false) die
  // fade-out-Klasse einen Frame BEVOR CarTopInfo intern auf die Chips
  // umschaltet -> der Subtitle blitzt eine Frame lang bei voller Deckkraft auf
  // ("Text -> Marke -> Text -> Marke"-Flackern beim allerersten Schritt).
  const ensureFadeInFor = (target) => {
    setAnimateOut(false);
    setSwapInvisible(true); // 👈 Slot verstecken, solange wir den Step tauschen
    setStep(target);
    nextStepRef.current = target; // Reveal-Marker bis der Gatekeeper feuert
    fadeOutDoneRef.current = false;
  };

  // Gatekeeper (wie Startseite): blendet erst ein, wenn der neue Step committed
  // UND der Slot versteckt ist. CarTopInfo hat im selben Passive-Effect-Flush
  // bereits auf die Chips umgeschaltet -> kein Aufblitzen des alten Subtitles.
  useEffect(() => {
    if (!swapInvisible) return;
    if (nextStepRef.current == null) return;
    setAnimateIn(true);
    setSwapInvisible(false);
    nextStepRef.current = null;
  }, [swapInvisible, step]);

  const onFadeOutEnd = (e) => {
    if (e.nativeEvent.animationName !== "fadeOut") return;
    // animateOut bleibt true, bis das (ggf. async) Ziel feststeht -> der
    // forwards-Fill der Fade-Out haelt opacity 0, kein Aufblitzen des alten Steps.
    fadeOutDoneRef.current = true;
    if (nextStepRef.current != null) {
      ensureFadeInFor(nextStepRef.current);
    }
  };

  const onFadeInEnd = (e) => {
    if (e.nativeEvent.animationName !== "fadeIn") return;
    setAnimateIn(false);
    setIsAnimating(false);
  };

  // ----------------------------------------
  // 3) Input-Handling (Autocomplete)
  // ----------------------------------------
  const brandAliases = {
    vw: "Volkswagen",
  };
  const handleInput = (
    val,
    options,
    setInput,
    setFiltered,
    setSelectedObj,
    currentStep
  ) => {
    const raw = val.trim();
    const lower = raw.toLowerCase();
    setInput(val);

    let filtered;
    // → SPEZIALFALL: nur im Step 1 (Marke) und exakt "vw"
    if (currentStep === 1 && lower === "vw") {
      filtered = options.filter(
        (o) => o.name.toLowerCase() === brandAliases.vw.toLowerCase()
      );
    } else {
      // normales StartsWith-Filtering für alle anderen Fälle
      filtered = options.filter((o) => o.name.toLowerCase().startsWith(lower));
    }

    setFiltered(filtered);

    // Sonderfall: kmStand (letzter Schritt)
    if (currentStep === 11) {
      setSelectedKmStand(val.trim());
      return;
    }

    // Exact Match
    const exactMatch = options.find(
      (o) => o.name.toString().toLowerCase() === val.trim().toLowerCase()
    );
    if (exactMatch) {
      setSelectedObj(exactMatch);
    } else {
      setSelectedObj({ id: "", name: "" });
    }
  };

  const handleSelect = (valObj, setInput, setSelectedObj, setFiltered) => {
    setInput(valObj.name);
    setSelectedObj(valObj);
    setFiltered([]);
  };

  const optionSettersByStep = useMemo(
    () => [
      setBrands,
      setModels,
      setYears,
      setBauformen,
      setFuels,
      setVariants,
      setPsOptions,
      setGearboxOptions,
      setDoorOptions,
      setModelTypeOptions,
    ],
    [
      setBrands,
      setModels,
      setYears,
      setBauformen,
      setFuels,
      setVariants,
      setPsOptions,
      setGearboxOptions,
      setDoorOptions,
      setModelTypeOptions,
    ]
  );

  const allSetInputs = useMemo(
    () => [
      setBrandInput,
      setModelInput,
      setYearInput,
      setBauformInput,
      setFuelInput,
      setVariantInput,
      setPsInput,
      setTransmissionInput,
      setDoorsInput,
      setModelTypeInput,
      setKmStandInput,
    ],
    [
      setBrandInput,
      setModelInput,
      setYearInput,
      setBauformInput,
      setFuelInput,
      setVariantInput,
      setPsInput,
      setTransmissionInput,
      setDoorsInput,
      setModelTypeInput,
      setKmStandInput,
    ]
  );

  const allSetSelected = useMemo(
    () => [
      setSelectedBrand,
      setSelectedModel,
      setSelectedYear,
      setSelectedBauform,
      setSelectedFuel,
      setSelectedVariant,
      setSelectedPs,
      setSelectedTransmission,
      setSelectedDoors,
      setSelectedModelType,
      setSelectedKmStand,
    ],
    [
      setSelectedBrand,
      setSelectedModel,
      setSelectedYear,
      setSelectedBauform,
      setSelectedFuel,
      setSelectedVariant,
      setSelectedPs,
      setSelectedTransmission,
      setSelectedDoors,
      setSelectedModelType,
      setSelectedKmStand,
    ]
  );

  const vehicleFlow = useMemo(
    () =>
      createVehicleFlow({
        api,
        setOptionsByStep: optionSettersByStep,
        setInputsByStep: allSetInputs,
        setSelectedByStep: allSetSelected,
        setFilteredItems,
        showToast,
        alwaysSkipVehicleSteps: [10],
      }),
    [
      api,
      optionSettersByStep,
      allSetInputs,
      allSetSelected,
      setFilteredItems,
    ]
  );

  useEffect(() => {
    vehicleFlow.loadBrands();
  }, [vehicleFlow]);

  useEffect(() => {
    setFilteredForStep([], step);
  }, [step]);

  const clearGuestRedirectTimer = () => {
    if (!guestRedirectTimerRef.current) return;
    window.clearTimeout(guestRedirectTimerRef.current);
    guestRedirectTimerRef.current = null;
  };

  const clearGuestRecoveryTimer = () => {
    if (!guestRecoveryTimerRef.current) return;
    window.clearTimeout(guestRecoveryTimerRef.current);
    guestRecoveryTimerRef.current = null;
  };

  const clearGuestTimers = () => {
    clearGuestRedirectTimer();
    clearGuestRecoveryTimer();
  };

  useEffect(
    () => () => {
      if (guestRedirectTimerRef.current) {
        window.clearTimeout(guestRedirectTimerRef.current);
        guestRedirectTimerRef.current = null;
      }
      if (guestRecoveryTimerRef.current) {
        window.clearTimeout(guestRecoveryTimerRef.current);
        guestRecoveryTimerRef.current = null;
      }
    },
    [],
  );

  const tryNavigate = (route) => {
    if (!route || didNavigateRef.current) return;
    didNavigateRef.current = true;
    clearGuestRedirectTimer();
    router.push(route);
  };

  const handleChevronAnimationEnd = (event) => {
    const name = event?.animationName;
    if (name !== "moveLastChevron" && name !== "moveChevronMobileAV") return;
    if (!pendingRouteRef.current) return;
    tryNavigate(pendingRouteRef.current);
  };

  // ----------------------------------------
  // 4) handleManualAdd mit Logs und Button-Disable
  // ----------------------------------------
  const handleManualAdd = async () => {
    if (!selectedKmStand) {
      showToast("Kein Kilometerstand angegeben", "error");
      return;
    }

    setIsManualSubmitting(true);
    setIsChevronAnimating(true);
    pendingRouteRef.current = "";
    didNavigateRef.current = false;
    clearGuestTimers();

    /* --- 2) Anonymer Nutzer → nur UI-Flow ------------------------- */
    if (!userData) {
      const pagePath =
        typeof window !== "undefined"
          ? window.location.pathname
          : "/auto-verkaufen";

      saveAnmeldenFlowPayload({
        title: "Bitte gib deine E-Mail-Adresse ein, um fortzufahren",
        fin: null,
        isManual: true,
        marke: selectedBrand,
        model: selectedModel,
        baujahr: selectedYear,
        bodytype: selectedBauform,
        fueltype: selectedFuel,
        variant: selectedVariant,
        ps: selectedPs,
        gearbox: selectedTransmission,
        doors: selectedDoors,
        modelType: selectedModelType,
        kmStand: selectedKmStand,
        page: pagePath,
      });

      pendingRouteRef.current = "/anmelden?fromFlow=1";
      guestRedirectTimerRef.current = window.setTimeout(() => {
        tryNavigate("/anmelden?fromFlow=1");
      }, 3200);
      guestRecoveryTimerRef.current = window.setTimeout(() => {
        guestRecoveryTimerRef.current = null;
        if (typeof window === "undefined") return;
        if (window.location.pathname !== pagePath) return;
        pendingRouteRef.current = "";
        didNavigateRef.current = false;
        setIsChevronAnimating(false);
        setIsManualSubmitting(false);
        showToast("Weiterleitung fehlgeschlagen. Bitte erneut versuchen.", "error");
      }, 6500);
      return;
    }

    /* --- 3) Payload für Backend ---------------------------------- */
    const payload = {
      email: userData.email,
      kmStand: selectedKmStand,
      marke: selectedBrand.name,
      model: selectedModel.name,
      variant: selectedVariant?.name ?? "",
      type: selectedModelType?.name ?? "",
      gearbox: selectedTransmission.name,
      fueltype: selectedFuel.name,
      bodytype: selectedBauform.name,
      baujahr: selectedYear.name,
      doors: selectedDoors.id,
      ps: selectedPs.name,
      vehicleDataIds: {
        brand: selectedBrand?.id ?? null,
        model: selectedModel?.id ?? null,
        year: selectedYear?.id ?? null,
        body: selectedBauform?.id ?? null,
        fuel: selectedFuel?.id ?? null,
        variant: selectedVariant?.id ?? selectedVariant?.name ?? null,
        kw: selectedPs?.id ?? null,
        gearbox: selectedTransmission?.id ?? null,
        doors: selectedDoors?.id ?? null,
        modelType: selectedModelType?.id ?? null,
      },
    };

    /* --- 4) Fetch-Promise (liefert id & vehicleMatchFound) -------- */
    const fetchPromise = (async () => {
      const res = await fetch(`${api}/user/add-vehicle-manually`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Fehler beim Anlegen");

      return {
        id: json.id,
        vehicleMatchFound: json.vehicleMatchFound,
        priceCalculationAutomated:
          json?.ab?.priceCalculationAutomated === true ||
          json?.priceCalculationAutomated === true,
      };
    })();

    /* --- 5) 3-Sekunden-Animation --------------------------------- */
    const animationPromise = new Promise((r) => setTimeout(r, 3000));

    try {
      /* warten, bis **beides** fertig ist */
      const { id, vehicleMatchFound, priceCalculationAutomated } =
        await Promise.all([
        fetchPromise,
        animationPromise,
      ]).then(([fetchRes]) => fetchRes);

      /* --- 6) Zielroute bestimmen --------------------------------- */
      trackVehicleCreated({ vehicleId: id, entrypoint: "auto-verkaufen" });

      const targetRoute = priceCalculationAutomated
        ? `/status/${id}`
        : vehicleMatchFound
          ? `/preis/${id}`
          : `/status/${id}`;
      router.push(targetRoute);
    } catch (err) {
      clearGuestTimers();
      setIsChevronAnimating(false);
      console.error("🚨 handleManualAdd Fehler:", err);
      showToast(err.message || "Unerwarteter Fehler", "error");
    } finally {
      setIsManualSubmitting(false);
    }
  };

  // ----------------------------------------
  // 5) Haupt-Handler für „Weiter“
  // ----------------------------------------
  const handleNext = async () => {
    if (isAnimating) return;
    setFilteredForStep([], step);

    if (
      userData &&
      Array.isArray(userData.fins) &&
      userData.fins.length >= 10
    ) {
      showToast(
        "Du hast die maximale Anzahl an Fahrzeugen in deinem Account erreicht. Entferne eines, um dieses Fahrzeug hinzuzufügen.",
        "error"
      );
      return;
    }

    if (step >= 11) {
      if (!selectedKmStand) {
        return showToast("Kein Kilometerstand angegeben", "error");
      }
      handleManualAdd();
      return;
    }

    // Leeres/ungueltiges Feld -> Fehlermeldung am Input (Referenz-Stil), nicht weiter.
    // Gueltig, wenn die Auswahl eine id ODER einen name hat (Flow nutzt z. B. fuer
    // Variante den Namen) -> blockiert nichts Valides.
    const currentSelection = [
      selectedBrand,
      selectedModel,
      selectedYear,
      selectedBauform,
      selectedFuel,
      selectedVariant,
      selectedPs,
      selectedTransmission,
      selectedDoors,
      selectedModelType,
    ][step - 1];
    if (!(currentSelection && (currentSelection.id || currentSelection.name))) {
      setInputError(
        {
          1: "Bitte gib eine gültige Automarke ein",
          2: "Bitte gib ein gültiges Modell ein",
          3: "Bitte wähle ein Baujahr",
          4: "Bitte wähle eine Bauform",
          5: "Bitte wähle einen Kraftstoff",
          6: "Bitte wähle eine Variante",
          7: "Bitte wähle den PS-Wert",
          8: "Bitte wähle ein Getriebe",
          9: "Bitte wähle die Türenanzahl",
          10: "Bitte wähle einen Modeltyp",
        }[step] || "Bitte triff eine gültige Auswahl",
      );
      return;
    }

    const target = await vehicleFlow.getNextStep({
      step,
      selections: {
        selectedBrand,
        selectedModel,
        selectedYear,
        selectedBauform,
        selectedFuel,
        selectedVariant,
        selectedPs,
        selectedTransmission,
        selectedDoors,
        selectedModelType,
      },
    });

    if (target == null || target === "STOP") return;

    nextStepRef.current = target;
    handleFadeOut();
  };

  // Zurueck-Navigation (wie Startseite): getBackStep raeumt die Auswahl ab
  // (resetFromStep), danach Fade-Out -> Step-Swap -> Fade-In ueber dieselbe
  // Mechanik wie handleNext.
  const handleBack = async () => {
    if (isAnimating) return;
    setFilteredForStep([], step);
    if (step <= 1) return;

    // Fade ZUERST starten, damit getBackStep die Auswahl waehrend des
    // Ausblendens loescht (Chips bleiben via animateOut-Guard) -> kein Flackern.
    handleFadeOut();
    if (step === 2) nextStepRef.current = 1;

    const found = await vehicleFlow.getBackStep({
      step,
      selections: {
        selectedBrand,
        selectedModel,
        selectedYear,
        selectedBauform,
        selectedFuel,
        selectedVariant,
        selectedPs,
        selectedTransmission,
        selectedDoors,
        selectedModelType,
      },
    });
    if (found == null || found === "STOP") {
      setAnimateOut(false);
      setIsAnimating(false);
      fadeOutDoneRef.current = false;
      nextStepRef.current = null;
      return;
    }
    nextStepRef.current = found;
    if (fadeOutDoneRef.current) ensureFadeInFor(found);
  };

  // Step-Labels + Fortschritt + Auswahl-Chips (wie auf der Startseite)
  const headers = [
    "",
    "Deine Automarke",
    "Dein Modell",
    "Baujahr",
    "Bauform",
    "Kraftstoff",
    "Variante",
    "PS-Wert",
    "Getriebe",
    "Türen",
    "Modeltyp",
    "Kilometerstand",
  ];
  const maxStep = headers.length - 1;
  const percentage =
    step > 0 ? (step === maxStep ? 99 : Math.round((step / maxStep) * 100)) : 0;

  const selectedChips = useMemo(() => {
    const arr = [];
    if (selectedBrand?.name)
      arr.push({ key: "brand", label: "Marke", value: selectedBrand.name });
    if (selectedModel?.name)
      arr.push({ key: "model", label: "Modell", value: selectedModel.name });
    if (selectedYear?.name)
      arr.push({ key: "year", label: "Baujahr", value: selectedYear.name });
    if (selectedBauform?.name)
      arr.push({ key: "bau", label: "Bauform", value: selectedBauform.name });
    if (selectedFuel?.name)
      arr.push({ key: "fuel", label: "Kraftstoff", value: selectedFuel.name });
    if (selectedVariant?.name)
      arr.push({ key: "var", label: "Variante", value: selectedVariant.name });
    if (selectedPs?.name)
      arr.push({ key: "ps", label: "PS", value: selectedPs.name });
    if (selectedTransmission?.name)
      arr.push({
        key: "gb",
        label: "Getriebe",
        value: selectedTransmission.name,
      });
    if (selectedDoors?.id)
      arr.push({
        key: "doors",
        label: "Türen",
        value: String(selectedDoors.id),
      });
    if (selectedModelType?.name)
      arr.push({ key: "mt", label: "Modeltyp", value: selectedModelType.name });
    if (selectedKmStand)
      arr.push({
        key: "km",
        label: "Kilometer",
        value: String(selectedKmStand),
      });
    return arr.slice(0, Math.max(0, step - 1));
  }, [
    step,
    selectedBrand?.name,
    selectedModel?.name,
    selectedYear?.name,
    selectedBauform?.name,
    selectedFuel?.name,
    selectedVariant?.name,
    selectedPs?.name,
    selectedTransmission?.name,
    selectedDoors?.id,
    selectedModelType?.name,
    selectedKmStand,
  ]);

  // ----------------------------------------------------
  // Mapping-Arrays für Autocomplete-Komponente (1–11)
  // ----------------------------------------------------
  const allOptions = [
    brands,
    models,
    years,
    bauformen,
    fuels,
    variants,
    psOptions,
    gearboxOptions,
    doorOptions,
    modelTypeOptions,
    [], // kmStand → kein Dropdown
  ];
  const allInputs = [
    brandInput,
    modelInput,
    yearInput,
    bauformInput,
    fuelInput,
    variantInput,
    psInput,
    transmissionInput,
    doorsInput,
    modelTypeInput,
    kmStandInput,
  ];

  // Platzhalter und Input-Typen pro Schritt
  const placeholders = [
    "Audi, VW, BMW…",
    "A3, X5…",
    "2020",
    "Limousine, SUV…",
    "Diesel, Benzin…",
    "30 TDI…",
    "116",
    "Automatik, Schaltgetriebe",
    "Z. B. 3 oder 5",
    "Modelvariante",
    "Kilometerstand",
  ];
  const inputTypes = [
    "text",
    "text",
    "text",
    "text",
    "text",
    "text",
    "text",
    "text",
    "text",
    "text",
    "number",
  ];

  // Step 12 renders the AnmeldenSection in the parent page.
  // Avoid keeping an empty collapsing hero wrapper in the DOM.
  if (step === 12) return null;

  return (
    <section className="hero">
      <div
        className={[
          "section-container",
          "AutoVerkaufen-container",
          className,
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {step !== 12 && (
          <>
            <div className="AutoVerkaufen-Top-Animation">
              <div
                className={`Chevron-Animation-SoFunktionierts-first ${
                  isChevronAnimating ? "animate" : ""
                }`}
              />

              <div
                className={`Chevron-Animation-SoFunktionierts ${
                  isChevronAnimating ? "animate" : ""
                }`}
              ></div>
              <div
                className={`Chevron-Animation-SoFunktionierts ${
                  isChevronAnimating ? "animate delay-1" : ""
                }`}
              ></div>
              <div
                className={`Chevron-Animation-SoFunktionierts ${
                  isChevronAnimating ? "animate delay-2" : ""
                }`}
              ></div>
              <div
                className={`Chevron-Animation-SoFunktionierts ${
                  isChevronAnimating ? "animate delay-3" : ""
                }`}
              ></div>
              <div
                className={`Chevron-Animation-SoFunktionierts-last ${
                  isChevronAnimating ? "animate delay-4" : ""
                }`}
                onAnimationEnd={handleChevronAnimationEnd}
              ></div>
              <div className="AutoVerkaufen-Top-Cars">
                <Image
                  src={blackCar}
                  alt="Schwarzes Auto für Verkauf"
                  className={`AutoVerkaufen-Top-blackCar ${
                    isChevronAnimating ? "animate" : ""
                  }`}
                  loading="lazy"
                  sizes="(max-width: 1040px) 240px, 360px"
                  quality={55}
                />
                <Image
                  src={whiteCar}
                  alt="Weißes Auto für Verkauf"
                  className={`AutoVerkaufen-Top-whiteCar ${
                    isChevronAnimating ? "animate" : ""
                  }`}
                  loading="lazy"
                  fetchPriority="low"
                  sizes="(max-width: 1040px) 220px, 360px"
                  quality={55}
                />
              </div>
            </div>
            <div className="AutoVerkaufen-Top">
              <nav className="AutoVerkaufen-navigation">
                <Link href="/" className="AutoVerkaufen-navigation-text">
                  Startseite
                </Link>

                {!removeNav ||
                  (customNav && (
                    <>
                      <span className="nav-divider">/</span>

                      <Link
                        href="/auto-verkaufen"
                        className="AutoVerkaufen-navigation-text"
                      >
                        Auto Verkaufen
                      </Link>
                    </>
                  ))}

                {customNav && (
                  <>
                    <span className="nav-divider">/</span>

                    <Link
                      href={customNavLink}
                      className="AutoVerkaufen-navigation-text"
                    >
                      {customNav}
                    </Link>
                  </>
                )}

                {link && (
                  <>
                    <span className="nav-divider">/</span>
                    <Link href={link} className="AutoVerkaufen-navigation-text">
                      {linkname}
                    </Link>
                  </>
                )}
              </nav>

              <h1>{title}</h1>
              <CarTopInfo
                items={selectedChips}
                textNode={<p>{text}</p>}
                reserveTextSpace
                shouldAnimate={step === 1 || nextStepRef.current === 1}
                animateOut={animateOut}
                animateIn={animateIn}
                hidden={
                  swapInvisible || (isAnimating && !animateOut && !animateIn)
                }
              />

              <div
                className={`FIN-Button-wrapper ${
                  animateOut ? "fade-out" : animateIn ? "fade-in" : ""
                } ${swapInvisible ? "is-hidden" : ""}`}
                onAnimationEnd={
                  animateOut ? onFadeOutEnd : animateIn ? onFadeInEnd : null
                }
              >
                <p className="Car-top-FIN-AV desktop">
                  <strong>{headers[step]}</strong>
                  {step > 1 && (
                    <>
                      &nbsp;
                      <span className="step-percentage">
                        ({percentage}% abgeschlossen)
                      </span>
                      <button type="button" className="back-button" onClick={handleBack}>
                        Zurück
                      </button>
                    </>
                  )}
                </p>
                <div className="AV-Input-wrapper">
                  <p className="Car-top-FIN-AV mobile">
                    <strong>{headers[step]}</strong>
                    {step > 1 && (
                      <>
                        &nbsp;
                        <span className="step-percentage">
                          ({percentage}% abgeschlossen)
                        </span>
                        <button type="button" className="back-button" onClick={handleBack}>
                          Zurück
                        </button>
                      </>
                    )}
                  </p>

                  <div className="Car-top-input-values">
                    <Input
                      type={inputTypes[step - 1]}
                      value={allInputs[step - 1]}
                      placeholder={placeholders[step - 1]}
                      onFocus={() => {
                        setInputError("");
                        setFilteredForStep(allOptions[step - 1], step);
                      }}
                      valid={
                        !![
                          selectedBrand.id,
                          selectedModel.id,
                          selectedYear.id,
                          selectedBauform.id,
                          selectedFuel.id,
                          selectedVariant.id,
                          selectedPs.id,
                          selectedTransmission.id,
                          selectedDoors.id,
                          selectedModelType.id,
                          selectedKmStand,
                        ][step - 1]
                      }
                      onBlur={() => setFilteredForStep([], step)}
                      onChange={(e) => {
                        setInputError("");
                        handleInput(
                          e.target.value,
                          allOptions[step - 1],
                          allSetInputs[step - 1],
                          (allFiltered) => setFilteredForStep(allFiltered, step),
                          allSetSelected[step - 1],
                          step
                        );
                      }}
                      inputMode={
                        step === 3 || step === 11 ? "numeric" : undefined
                      }
                      pattern={step === 3 || step === 11 ? "[0-9]*" : undefined}
                      errorMessage={inputError}
                    />
                    {filteredItems.length > 0 && filteredItemsStep === step && (
                      <div className="Input-dropdown-shell">
                        <ul className="Input-dropdown-values">
                        {filteredItems.map((o, i) => (
                          <li
                            key={i}
                            className="Input-dropdown-value"
                            onMouseDown={() =>
                              handleSelect(
                                o,
                                allSetInputs[step - 1],
                                allSetSelected[step - 1],
                                (items) => setFilteredForStep(items, step)
                              )
                            }
                          >
                            {o.name}
                          </li>
                        ))}
                      </ul>
                      </div>
                    )}
                  </div>

                  {/* Desktop-Button */}
                  <ButtonAnimated
                    onClick={handleNext}
                    className="AV-Button-top desktop"
                    tooltipPosition="top"
                    text={step === 1 || step >= 11 ? "Auto bewerten" : "Weiter"}
                    disabled={step === 11 && isManualSubmitting} // Button deaktivieren, sobald handleManualAdd läuft
                  />

                  {/* Mobile-Button */}
                  <ButtonAnimated
                    onClick={handleNext}
                    className="AV-Button-top mobile"
                    tooltipPosition="bottom"
                    text={step === 1 || step >= 11 ? "Auto bewerten" : "Weiter"}
                    disabled={step === 11 && isManualSubmitting} // Button deaktivieren, sobald handleManualAdd läuft
                  />

                  <TrustPilot />
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
};
